/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import StateInterface from "../../redux-magic/state-interface";
import {
	setSearchAnimationDetailsThunk,
	setSearchFilterMetricesThunk,
	updateSearchFilterAppliedThunk,
	updateSearchFilterSelectedOptionsThunk,
} from "../../redux-magic/thunks";

/* Component Imports */

import { Chip, Divider, IconButton, Stack, SwipeableDrawer, Typography, useTheme } from "@mui/material";
import { useRouter } from "next/router";
import LocationAutoCompleteMobile from "../page-sections/Index/SearchMobileComponent/LocationAutoCompleteMobile";

/* Icon Imports */

import { Close } from "@mui/icons-material";

/* Styled Components */

const CustomSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => ({
	"& .MuiDrawer-paper": {
		display: "flex",
		flexDirection: "column",
		justifyContent: "space-between",
		alignItems: "space-between",
		padding: "0.5rem 0.5rem 0.5rem 0.5rem",
		borderRadius: "8px 8px 0px 0px",
		gap: "0.5rem",
		width: "100%",
		background: theme.palette.background.default,
		maxHeight: "100%",
	},
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	padding: "0rem",
	gap: "0rem",
	overflow: "hidden",
}));

const LocationChips = styled(Chip)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	borderRadius: "8px",
	gap: "0.5rem",
	"& .MuiChip-label": {
		padding: "0rem",
		fontWeight: 400,
	},
	fontSize: "0.875rem",
	padding: "0.25rem 0.5rem 0.25rem 0.5rem",
})) as typeof Chip;

const SearchLocationModalMobile = ({
	openSearchLocation,
	dispatch,
	search_filter_state,
	searchanimationsettings,
	searchLocation,
}: {
	openSearchLocation: boolean;
	dispatch: any;
	search_filter_state: StateInterface["search_filters_state"];
	searchanimationsettings: StateInterface["search_animation_settings"];
	searchLocation: any;
}) => {
	const theme = useTheme();

	const router = React.useRef(useRouter()).current;

	const localLang = {
		enter_search_location:
			router.locale === "ar-AE"
				? "أدخل موقع البحث"
				: router.locale === "kn-IN"
					? "ಹುಡುಕುವ ಸ್ಥಳವನ್ನು ನಮೂದಿಸಿ"
					: "Enter search location",
		or: router.locale === "ar-AE" ? "أو" : "OR",
		try_one_of_these_locations:
			router.locale === "ar-AE"
				? "جرّب واحدة من هذه المناطق الشهيرة في بنغالورو"
				: router.locale === "kn-IN"
					? "ಬೆಂಗಳೂರಿನ ಈ ಜನಪ್ರಿಯ ಪ್ರದೇಶಗಳಲ್ಲಿ ಒಂದನ್ನು ಆಯ್ಕೆ ಮಾಡಿ"
					: "Try one of these popular areas in Bengaluru",
	};

	/*

		* Handle click location

	*/

	const locationSelect: string[] = [
		"HSR Layout",
		"Sarjapur Road",
		"Koramangala",
		"BTM Layout",
		"Jayanagar",
		"Electronic City",
		"Whitefield",
		"Varthur",
		"Bellandur",
		"Bannerghatta Road",
		"Kannakapura Road",
	];

	const [SubCategoryChecked, setSubCategoryChecked] = React.useState<string>(
		typeof router.query?.location === "string" ? router.query?.location.split(",")[0] : "",
	);

	React.useEffect(() => {
		if (router.query?.location) {
			setSubCategoryChecked(typeof router.query?.location === "string" ? router.query?.location.split(",")[0] : "");
		}
	}, [router.query?.location]);

	const handleClickLocationChips = (data: string) => {
		setSubCategoryChecked(data);

		const locationCoordinates: Record<string, [number, number]> = {
			"HSR Layout": [12.9121, 77.6446],
			"Sarjapur Road": [12.854922, 77.788116],
			Koramangala: [12.9352, 77.6245],
			"BTM Layout": [12.9166, 77.6101],
			Jayanagar: [12.9308, 77.5839],
			"Electronic City": [12.839, 77.679],
			Whitefield: [12.9698, 77.75],
			Varthur: [12.9366, 77.7413],
			Bellandur: [12.9304, 77.6784],
			"Bannerghatta Road": [12.8345, 77.6058],
			"Kannakapura Road": [12.8342, 77.5655],
		};

		const [lat, lng] = locationCoordinates[data] || [null, null];

		if (!lat || !lng) return;

		dispatch(
			updateSearchFilterSelectedOptionsThunk({
				searchFilterId: "location",
				newOptions: [
					{
						id: "location",
						title: "Location",
						value: {
							title: data,
							coordinates: [lng, lat],
						},
					},
				],
			}),
		);

		dispatch(
			updateSearchFilterAppliedThunk({
				searchFilterId: "location",
				isApplied: true,
			}),
		);

		if (router.asPath.startsWith("/search") && search_filter_state?.location?.selected_options?.length) {
			// dispatch(
			// 	setSearchAnimationDetailsThunk({
			// 		StartAnimate: true,
			// 		TriggerSearch: true,
			// 		PropertySubCategory: ["Budget", "Status", "Listed by", "Facing"],
			// 		ResetSearch: true,
			// 		HomeAccordion: false,
			// 		OpenLocationDrawer: false,
			// 	}),
			// );
			dispatch(
				setSearchFilterMetricesThunk({
					boosted: [0],
					non_boosted: [0],
					filter_hash: "",
					no_of_boosted_results: 0,
					no_of_non_boosted_results: 0,
					total_no_of_results: 0,
					boosted_from_other_locations: [0],
					no_of_boosted_from_other_locations: 0,
				}),
			);

			const defaultUrlpath = encodeURIComponent(
				`${process.env.PRODUCTION_URL || ""}/search?search_type=properties&location=${data},${lng}, ${lat}&transaction_type=rent&property_category=residential&property_type=Apartment&locale=${router.locale}`,
			);

			const newUrl = new URL(window.location.href);
			newUrl.searchParams.set("location", `${data},${lng},${lat}`);

			router.push(
				`/loading.html?callbackUrl=${
					search_filter_state.property_category.selected_options.length > 0 ||
					search_filter_state.property_type.selected_options.length > 0 ||
					search_filter_state.service_subtype.selected_options.length > 0 ||
					search_filter_state.service_type.selected_options.length > 0
						? encodeURIComponent(newUrl.toString())
						: defaultUrlpath
				}`,
			);
		} else {
			const developerApplied = search_filter_state.listed_by.is_applied ? "&listed_by=developer" : "";

			const defaultUrlpath = encodeURIComponent(
				`${process.env.PRODUCTION_URL || ""}/search?search_type=properties&location=${data},${lng}, ${lat}&transaction_type=rent&property_category=residential&property_type=Apartment&locale=${router.locale}`,
			);

			const urlPathToRedirect =
				search_filter_state.search_type.selected_options[0].id === "properties"
					? encodeURIComponent(
							`${process.env.PRODUCTION_URL || ""}/search?search_type=properties&location=${data},${lng},${lat}&transaction_type=${search_filter_state.transaction_type.selected_options[0]?.id}&property_category=${search_filter_state.property_category.selected_options[0]?.id}&property_type=${search_filter_state.property_type.selected_options[0]?.value}${developerApplied}&locale=${router.locale}`,
						)
					: search_filter_state.search_type.selected_options[0].id === "services"
						? encodeURIComponent(
								`${process.env.PRODUCTION_URL || ""}/search?search_type=services&location=${data},${lng}, ${lat}&service_type=${search_filter_state.service_type.selected_options[0]?.id}&service_subtype=${search_filter_state.service_subtype.selected_options[0]?.id}&locale=${router.locale}`,
							)
						: null;

			router.push(
				`/loading.html?callbackUrl=${
					search_filter_state.property_category.selected_options.length > 0 ||
					search_filter_state.property_type.selected_options.length > 0 ||
					search_filter_state.service_subtype.selected_options.length > 0 ||
					search_filter_state.service_type.selected_options.length > 0
						? urlPathToRedirect
						: defaultUrlpath
				}`,
			);

			dispatch(
				setSearchAnimationDetailsThunk({
					StartAnimate: searchanimationsettings?.StartAnimate as boolean,
					TriggerSearch: searchanimationsettings?.TriggerSearch as boolean,
					ResetSearch: searchanimationsettings?.ResetSearch as boolean,
					PropertySubCategory: ["Budget", "Status", "Listed by", "Facing"],
					HomeAccordion: false,
					OpenLocationDrawer: false,
				}),
			);
		}
	};

	return (
		<CustomSwipeableDrawer
			anchor="bottom"
			open={openSearchLocation}
			onClose={() => {
				dispatch(
					setSearchAnimationDetailsThunk({
						StartAnimate: searchanimationsettings?.StartAnimate as boolean,
						TriggerSearch: searchanimationsettings?.TriggerSearch as boolean,
						ResetSearch: searchanimationsettings?.ResetSearch as boolean,
						PropertySubCategory: ["Budget", "Status", "Listed by", "Facing"],
						HomeAccordion: false,
						OpenLocationDrawer: false,
					}),
				);
			}}
			onOpen={() => {
				dispatch(
					setSearchAnimationDetailsThunk({
						StartAnimate: searchanimationsettings?.StartAnimate as boolean,
						TriggerSearch: searchanimationsettings?.TriggerSearch as boolean,
						ResetSearch: searchanimationsettings?.ResetSearch as boolean,
						PropertySubCategory: ["Budget", "Status", "Listed by", "Facing"],
						HomeAccordion: false,
						OpenLocationDrawer: true,
					}),
				);
			}}
			swipeAreaWidth={156}
			disableSwipeToOpen={true}
			ModalProps={{
				keepMounted: true,
			}}
			disableBackdropTransition={true}
			sx={{
				"& .MuiDrawer-paper": {
					background: theme.palette.background.paper,
					padding: "1rem",
					direction: router.locale === "ar-AE" ? "rtl" : "ltr",
					height: "100vh",
				},
			}}
		>
			<ContentContainer
				sx={{
					gap: "0.5rem",
				}}
			>
				<div
					style={{
						display: "flex",
						width: "100%",
						justifyContent: "space-between",
					}}
				>
					<Typography
						id="search_location_heading"
						variant="subtitle2"
						sx={{
							alignSelf: "center",
						}}
					>
						{localLang.enter_search_location}
					</Typography>
					<IconButton
						onClick={() => {
							dispatch(
								setSearchAnimationDetailsThunk({
									StartAnimate: searchanimationsettings?.StartAnimate as boolean,
									TriggerSearch: searchanimationsettings?.TriggerSearch as boolean,
									ResetSearch: searchanimationsettings?.ResetSearch as boolean,
									PropertySubCategory: ["Budget", "Status", "Listed by", "Facing"],
									HomeAccordion: false,
									OpenLocationDrawer: false,
								}),
							);
						}}
					>
						<Close />
					</IconButton>
				</div>

				<LocationAutoCompleteMobile
					search_id="location"
					dispatch={dispatch}
					location={search_filter_state?.location as StateInterface["search_filters_state"]["location"]}
					search_filter_state={search_filter_state as StateInterface["search_filters_state"]}
					searchLocation={searchLocation}
				/>

				<div
					style={{
						display: "flex",
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "center",
						gap: "8px",
						width: "100%",
						margin: "0.5rem 0rem 0.5rem 0rem",
					}}
				>
					<Divider style={{ flex: 1 }} />

					<span style={{ padding: "0 8px", fontSize: "1rem", color: "#333" }}>{localLang.or}</span>
					<Divider style={{ flex: 1 }} />
				</div>

				<Stack
					sx={{
						display: "flex",
						flexDirection: "row",
						flexWrap: "wrap",
						gap: "0.5rem",
						"&::-webkit-scrollbar": {
							display: "none",
						},
					}}
				>
					<Typography
						variant="body2"
						sx={{ padding: "0rem 0rem 0.5rem 0rem", fontWeight: 500, width: "100%" }}
					>
						{localLang.try_one_of_these_locations}
					</Typography>

					{locationSelect?.map((data: any, id: any) => (
						<LocationChips
							id={"location_chips" + data}
							key={id}
							sx={{
								backgroundColor: SubCategoryChecked === data ? "rgba(252, 128, 25, 0.16)" : "",
								color: "black",
								border: SubCategoryChecked === data ? "1px solid #FC8019" : "1px solid #C0C0C0",

								"&.MuiChip-clickable:hover": {
									backgroundColor: SubCategoryChecked === data ? "rgba(252, 128, 25, 0.16)" : "",
								},
								"& .MuiChip-label": {
									fontWeight: SubCategoryChecked === data ? 500 : 400,
								},
							}}
							label={data}
							clickable
							variant="outlined"
							onClick={() => {
								SubCategoryChecked === data ? handleClickLocationChips(data) : handleClickLocationChips(data);
							}}
						/>
					))}
				</Stack>
			</ContentContainer>
		</CustomSwipeableDrawer>
	);
};

export default SearchLocationModalMobile;
